import React, { useEffect, useId, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { FiRefreshCw, FiX, FiKey, FiArrowRight, FiChevronDown } from 'react-icons/fi';
import { readAccounts, readPreference, subscribeAccounts } from '../_services/savedAccounts';
import { getUserRoleLabel } from '../utils/roleConstants';
import { userService } from '../_services/user.service';
import '../Assets/AccountPicker.css';

const MODES = [
    { value: 'none', label: "Don't save accounts", detail: 'Sign in with your username and password each time.' },
    { value: 'password', label: 'Save accounts and ask for a password', detail: 'Remember the account name and enter its password to sign in.' },
    { value: 'one_click', label: 'Save accounts for one-click login', detail: 'Switch to a saved account without entering its password on this browser.' },
];

export function SavePreferenceOptions({ value, onChange, disabled }) {
    const groupId = useId();
    return <fieldset className="account-save-options" disabled={disabled}>
        <legend className="visually-hidden">How to save accounts on this browser</legend>
        {MODES.map(mode => <label className={`account-save-option ${value === mode.value ? 'selected' : ''}`} key={mode.value}>
            <input
                type="radio"
                name={groupId}
                value={mode.value}
                aria-label={mode.label}
                aria-describedby={`${groupId}-${mode.value}`}
                checked={value === mode.value}
                onChange={() => onChange(mode.value)}
            />
            <span>
                <span className="account-option-label">{mode.label}</span>
                <span className="account-option-detail" id={`${groupId}-${mode.value}`}>{mode.detail}</span>
            </span>
        </label>)}
    </fieldset>;
}

export default function AccountPicker({ currentUser, onSelect, compact = false, disabled = false }) {
    const [accounts, setAccounts] = useState(readAccounts);
    const [open, setOpen] = useState(false);
    const [editingPreference, setEditingPreference] = useState(false);
    const [preference, setPreference] = useState(readPreference);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // The login-page list starts collapsed; the sidebar modal is always expanded.
    const [expanded, setExpanded] = useState(compact);
    const listId = useId();

    useEffect(() => subscribeAccounts(() => {
        setAccounts(readAccounts());
        if (!editingPreference) setPreference(readPreference());
    }), [editingPreference]);

    async function remove(account) {
        if (busy || disabled) return;

        setBusy(true);
        setError('');
        try {
            await userService.forgetAccount(account);
        } catch (failure) {
            setError(failure.message || 'This account could not be removed. Please retry.');
        } finally {
            setBusy(false);
        }
    }

    async function savePreference() {
        if (busy || !preference) return;

        setBusy(true);
        setError('');
        try {
            await userService.changeSavePreference(preference);
            setEditingPreference(false);
        } catch (failure) {
            setError(failure.message || 'The preference could not be saved. Please retry.');
        } finally {
            setBusy(false);
        }
    }

    const body = <>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {editingPreference ? <>
            <p className="account-picker-note">This choice applies to all accounts saved on this browser.</p>
            <SavePreferenceOptions value={preference} onChange={setPreference} disabled={busy} />
            <div className="account-picker-actions">
                <Button variant="outline-secondary" disabled={busy} onClick={() => setEditingPreference(false)}>Cancel</Button>
                <Button disabled={busy || !preference} onClick={savePreference}>{busy ? 'Saving…' : 'Save preference'}</Button>
            </div>
        </> : <>
            {accounts.length === 0 && <p className="account-picker-note">Accounts you save at logout will appear here.</p>}
            <div className="account-picker-list">
                {accounts.map(account => {
                    const current = String(currentUser?.id) === account.id;
                    const displayName = [account.firstName, account.lastName].filter(Boolean).join(' ') || account.username;
                    // Entries saved before roles were stored have no badge until their next login refreshes them.
                    const roleLabel = account.role != null || account.superAdmin
                        ? getUserRoleLabel({ role: account.role, is_superadmin: account.superAdmin })
                        : '';
                    return <div className="saved-account-row" key={account.id}>
                        <button
                            type="button"
                            className="saved-account-select"
                            disabled={busy || disabled || current}
                            onClick={() => {
                                setOpen(false);
                                onSelect(account);
                            }}
                        >
                            <span className="saved-account-avatar" aria-hidden="true">{displayName.charAt(0).toUpperCase()}</span>
                            <span className="saved-account-text">
                                <span className="saved-account-title">
                                    <span className="saved-account-name">{displayName}</span>
                                    {roleLabel && <span className="saved-account-role">{roleLabel}</span>}
                                </span>
                                {account.companyName && <span className="saved-account-detail">{account.companyName}</span>}
                                <span className="saved-account-mode">{current ? 'Current account' : account.mode === 'one_click' ? 'One-click login' : 'Password required'}</span>
                            </span>
                            {account.mode === 'one_click' ? <FiArrowRight aria-hidden="true" /> : <FiKey aria-hidden="true" />}
                        </button>
                        <button
                            type="button"
                            className="saved-account-remove"
                            aria-label={`Remove ${account.username}`}
                            title="Remove saved account"
                            disabled={busy || disabled}
                            onClick={() => remove(account)}
                        >
                            <FiX aria-hidden="true" />
                        </button>
                    </div>;
                })}
            </div>
            <button type="button" className="account-preference-link" disabled={busy || disabled} onClick={() => {
                setError('');
                setPreference(readPreference());
                setEditingPreference(true);
            }}>Change save preference</button>
        </>}
    </>;

    const content = compact ? <div className="account-picker-content">{body}</div> : <div className="account-picker-content">
        <button
            type="button"
            className="account-picker-heading account-picker-toggle"
            aria-expanded={expanded}
            aria-controls={listId}
            onClick={() => setExpanded(value => !value)}
        >
            <span className="account-picker-title">
                Saved accounts
                <FiChevronDown className={`account-picker-chevron ${expanded ? 'expanded' : ''}`} aria-hidden="true" />
            </span>
        </button>
        {expanded && <div id={listId}>{body}</div>}
    </div>;

    if (!compact) return content;

    return <>
        <button
            type="button"
            className="sidebar-account-switch"
            title="Switch account"
            aria-label="Switch account"
            disabled={disabled}
            onClick={() => {
                setError('');
                setOpen(true);
            }}
        >
            <FiRefreshCw aria-hidden="true" />
        </button>
        <Modal show={open} onHide={() => { if (!busy) setOpen(false); }} centered>
            <Modal.Header closeButton={!busy}><Modal.Title>Saved accounts <span className="account-count">{accounts.length}/5</span></Modal.Title></Modal.Header>
            <Modal.Body>{content}</Modal.Body>
        </Modal>
    </>;
}
